// Goods that came back to the seller: a parcel the buyer never collected or
// refused at the door, or goods sent back within the return period. The
// return is recorded with why (a reason from a fixed list, so returns can be
// counted later) and whether the goods can be sold again; the money is the
// refund's business, which the dialog offers to open right after
// (aldoo-na/store/returns, beside aldoo-na/store/refunds).
import React, { useMemo, useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import { ExclamationTriangleIcon, InformationCircleIcon } from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"
import { describeLine } from "aldoo-ra/Store/lines"

/** What each reason (aldoo-na/store/returns REASONS) is called, in English: said through t(). */
export const REASON_LABELS = {
  undelivered: "Not collected or refused",
  withdrawal: "Changed their mind (withdrawal)",
  wrong_size: "Wrong size or fit",
  not_as_described: "Not as described",
  wrong_item: "Wrong item sent",
  defective: "Defective",
  damaged_in_transit: "Damaged in transit",
  other: "Other",
}

/** The reasons the server knows, with what each suggests for the shelf; the list here when it sends none. */
const reasonsOf = (order) =>
  order?.returned?.reasons?.length ? order.returned.reasons : Object.keys(REASON_LABELS).map((id) => ({ id, restock: !["defective", "damaged_in_transit"].includes(id), note: id === "other" }))

/**
 * `order`: the order as the order page has it (with `returned`, the server's
 * reading of what came back and the return period). `onReturn({ lines,
 * reason, note, restock })` records it and answers `{ error, message }` or
 * the saved state. `onClose(result)`: `{ lines, refundNext }` once recorded.
 */
export default function ReturnDialog({ order, t, locale = "en-GB", onClose, onReturn, height = null, preset = null }) {
  const reasons = useMemo(() => reasonsOf(order), [order])
  // what of each cart line can still come back, with the line as the order shows it
  const lines = useMemo(
    () =>
      (order?.returned?.lines || [])
        .filter((line) => line.left > 0)
        .map((line) => {
          const item = order.cart?.[line.cartIndex] || {}
          return { ...line, id: String(line.cartIndex), title: describeLine(item, item.name || line.pid), thumbnail: item.thumbnail || null }
        }),
    [order]
  )
  // the buyer's accepted requests whose goods are still to come: what came back is their items
  // together — each line says which request asked for it; the rest of the order is a click away
  const requests = preset?.requests || []
  const lineOfRequest = (want) => lines.find((entry) => (want.lineKey ? entry.lineKey === want.lineKey : entry.pid === want.pid))
  // the request that asked for each line, by line id (a unit is never in two waiting requests)
  const requestOfLine = useMemo(() => {
    const by = {}
    for (const request of requests) for (const want of request.lines || []) {
      const line = lineOfRequest(want)
      if (line && !by[line.id]) by[line.id] = request
    }
    return by
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests, lines])
  const askedBy = useMemo(() => Object.fromEntries(Object.entries(requestOfLine).map(([id, request]) => [id, [request.number]])), [requestOfLine])
  // from requests: only what was asked for is listed; without requests, every item that can come back
  const shownLines = requests.length ? lines.filter((line) => askedBy[line.id]) : lines
  // the reason and note the seller gives: for a return without requests, or for items taken in
  // that nobody asked for — a requested item comes back with its own request's reason and note
  const [reason, setReason] = useState(requests.length ? "" : preset?.reason || "")
  const [note, setNote] = useState(requests.length ? "" : preset?.note || "")
  // nothing is taken in until the seller says so: parcels of several requests arrive one by one,
  // so each line starts at none (off) and is set for what is actually in hand
  const [quantities, setQuantities] = useState(() => Object.fromEntries(lines.map((line) => [line.id, "0"])))
  // whether the goods go back on sale: what the reason suggests, until it is set by hand
  const [restock, setRestock] = useState(null)
  const refundable = Math.max(0, (order?.amount ?? 0) - (order?.refundedAmount ?? 0))
  const [refundNext, setRefundNext] = useState(refundable > 0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const chosen = reasons.find((entry) => entry.id === reason) || null
  const clamp = (value, left) => Math.max(0, Math.min(Math.floor(Number(value) || 0), left))
  const picked = lines.map((line) => ({ ...line, quantity: clamp(quantities[line.id], line.left) })).filter((line) => line.quantity > 0)
  // the items taken in, by the request that asked for them; the rest is the seller's own
  const groups = requests
    .map((request) => ({ request, lines: picked.filter((line) => requestOfLine[line.id] === request) }))
    .filter((group) => group.lines.length)
  const unrequested = picked.filter((line) => !requestOfLine[line.id])
  // the seller says why only for what nobody asked for (or when there are no requests at all)
  const needsReason = unrequested.length > 0
  // back on sale only when the seller says so: what came back is checked before it is sold again
  const shelf = restock ?? false
  const noteMissing = needsReason && !!chosen?.note && !note.trim()
  const ready = picked.length > 0 && (!needsReason || !!chosen) && !noteMissing

  const period = order?.returned?.window || {}
  const date = (d) => (d ? new Date(d).toLocaleDateString(locale, { dateStyle: "medium" }) : "")

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      const asLines = (list) => list.map(({ pid, lineKey, quantity }) => ({ pid, ...(lineKey ? { lineKey } : {}), quantity }))
      // one return per request, with its reason and note (so each is counted by its own why);
      // then what nobody asked for, with the seller's
      const parts = [
        ...groups.map(({ request, lines: own }) => ({ lines: asLines(own), reason: request.reason, note: request.note || undefined, request_ids: [String(request._id)] })),
        ...(unrequested.length ? [{ lines: asLines(unrequested), reason, note: note.trim() || undefined }] : []),
      ]
      for (const part of parts) {
        const res = await onReturn({ ...part, restock: shelf })
        if (res?.error) return setError(res.message || res.error)
      }
      onClose?.({ lines: picked.map(({ cartIndex, quantity }) => ({ cartIndex, quantity })), refundNext: refundNext && refundable > 0 })
    } catch (failure) {
      setError(failure?.message || t`The return could not be recorded`)
    } finally {
      setBusy(false)
    }
  }

  const options = reasons.map((entry) => ({ value: entry.id, name: t(REASON_LABELS[entry.id] || entry.id) }))

  return (
    <Modal
      isOpen
      onClose={() => onClose?.()}
      variant="admin_compact"
      title={t("Mark order %s as returned", order?.orderId || "")}
      containerStyle={height ? { height: `${Math.round(height)}px` } : undefined}
      footerContent={
        <div className="flex flex-wrap items-center gap-4">
          {/* offered only when there is money left to give back: a switch that cannot be used is no switch */}
          {refundable > 0 && (
            <label className="flex items-center gap-2 text-sm text-admin_text" data-testid="return-refund-next">
              <ToggleSwitch variant="admin" isOn={refundNext} onChange={(on) => setRefundNext(on)} />
              {t`Refund the returned goods next`}
            </label>
          )}
          <Button variant="admin_primary" className="ml-auto" onClick={submit} disabled={busy || !ready} aria-label="return-submit">
            {busy ? t`Saving…` : t`Mark as returned`}
          </Button>
        </div>
      }
      styleOverrides={{
        container: "bg-admin_paper border-admin_bordered w-full h-[100dvh] max-h-[100dvh] md:h-auto md:max-w-[900px] md:max-h-[94vh] overflow-hidden rounded-none md:rounded-md border shadow-lg",
        footer: "px-5 py-3 border-t border-admin_bordered bg-admin_paper",
      }}
    >
      <div className="flex flex-col gap-4 p-5 pt-3" data-testid="return-dialog">
        {/* the return period, from delivery: in it, past it (the seller may still
            take the goods), or none — a parcel that never reached the buyer */}
        {period.within === true && (
          <div className="flex items-start gap-2 rounded-md border border-admin_success/40 bg-admin_success/10 p-3 text-sm text-admin_text" data-testid="return-window">
            <InformationCircleIcon className="w-5 h-5 flex-shrink-0 text-admin_success" />
            <span>{t("Within the %s-day return period, which ends on %s.", period.days, date(period.until))}</span>
          </div>
        )}
        {period.within === false && (
          <div className="flex items-start gap-2 rounded-md border border-admin_warning/40 bg-admin_warning/10 p-3 text-sm text-admin_text" data-testid="return-window">
            <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 text-admin_warning" />
            <span>{t("The %s-day return period ended on %s. You may still take the goods back.", period.days, date(period.until))}</span>
          </div>
        )}
        {period.within === null && period.days && (
          <div className="flex items-start gap-2 rounded-md border border-admin_bordered bg-admin_neutral_1/50 p-3 text-sm text-admin_text" data-testid="return-window">
            <InformationCircleIcon className="w-5 h-5 flex-shrink-0 text-admin_text_secondary" />
            <span>{t`Not marked as delivered: a parcel that comes back undelivered.`}</span>
          </div>
        )}

        {/* asked only when the seller is the one saying why: no requests, or items nobody asked for */}
        {(!requests.length || needsReason) && (
          <>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-admin_text_secondary">{t`Reason`}</span>
          <div data-testid="return-reason">
            <DropdownSelect
              variant="admin"
              placeholder={t`Why did it come back?`}
              options={options}
              value={options.filter((option) => option.value === reason)}
              onChange={(value) => {
                const first = Array.isArray(value) ? value[0] : value
                if (first) setReason(first.value)
              }}
              searchEnabled={false}
              showCheckbox={false}
              showSelectionTick
              enableResetButton={false}
              allowEmptySelection={false}
              className="w-full"
            />
          </div>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-admin_text_secondary">{chosen?.note ? t`Note (required)` : t`Note (optional)`}</span>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t`What the buyer or the courier said`}
            className={twMerge("px-3 py-2 rounded-md border text-sm outline-none resize-y bg-admin_paper text-admin_text border-admin_bordered", noteMissing && "border-admin_error")}
            data-testid="return-note"
          />
        </label>
          </>
        )}

        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-admin_text_secondary">{t`What came back`}</span>
          <div className="border border-admin_bordered rounded-lg divide-y divide-admin_bordered/60">
            {shownLines.map((line) => (
              <div key={line.id} className="flex items-center gap-3 p-2.5 text-sm">
                <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0 bg-admin_neutral_1">
                  {line.thumbnail && <img src={line.thumbnail} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-admin_text">{line.title}</div>
                  <div className="text-xs text-admin_text_secondary truncate">
                    {askedBy[line.id] && <span className="font-medium text-admin_primary">{askedBy[line.id].map((number) => t("Return request #%s", number)).join(", ")} · </span>}
                    {line.sku ? `${line.sku} · ` : ""}
                    {t("%s of %s can come back", line.left, line.bought)}
                  </div>
                  {/* the buyer's why, from their request: nothing for the seller to type */}
                  {requestOfLine[line.id] && (
                    <div className="text-xs text-admin_text mt-0.5 whitespace-normal break-words" data-testid="return-line-reason">
                      <span className="font-medium">{t(REASON_LABELS[requestOfLine[line.id].reason] || requestOfLine[line.id].reason)}</span>
                      {requestOfLine[line.id].note ? <span className="text-admin_text_secondary"> — {requestOfLine[line.id].note}</span> : null}
                    </div>
                  )}
                </div>
                {/* one unit to take back: on or off, no number to type */}
                {line.left === 1 ? (
                  <div className="flex-shrink-0" data-testid="return-line-toggle">
                    <ToggleSwitch variant="admin" isOn={clamp(quantities[line.id], 1) === 1} onChange={(on) => setQuantities((current) => ({ ...current, [line.id]: on ? "1" : "0" }))} />
                  </div>
                ) : (
                <label className="flex items-center gap-2 flex-shrink-0">
                  <input
                    type="number"
                    min={0}
                    max={line.left}
                    step={1}
                    value={quantities[line.id] ?? 0}
                    onChange={(e) => {
                      const value = e.target.value
                      setQuantities((current) => ({ ...current, [line.id]: Number(value) > line.left ? String(line.left) : value }))
                    }}
                    onBlur={() => setQuantities((current) => ({ ...current, [line.id]: String(clamp(current[line.id], line.left)) }))}
                    className="w-[64px] h-9 px-2 rounded-md border text-right tabular-nums border-admin_bordered bg-admin_paper text-admin_text"
                    data-testid="return-line-quantity"
                  />
                  <span className="text-admin_text_secondary">{t`units`}</span>
                </label>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* sellable again or not: what the reason suggests (a defective or damaged item is not), set by hand otherwise */}
        <label className="flex items-center gap-3 text-sm text-admin_text" data-testid="return-restock">
          <ToggleSwitch variant="admin" isOn={shelf} onChange={(on) => setRestock(on)} />
          <span>
            {t`Put the goods back on sale`}
            <span className="block text-xs text-admin_text_secondary">{shelf ? t`They go back into stock.` : t`They stay out of stock: damaged or not sellable.`}</span>
          </span>
        </label>

        {error && (
          <div role="alert" className="flex items-start gap-2 rounded-md border border-admin_error/40 bg-admin_error/10 p-3 text-sm text-admin_error" data-testid="return-error">
            <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
            <div>
              <div className="font-semibold">{t`The return was not recorded`}</div>
              <div>{error}</div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
